import { type ReactNode, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { Icon } from '@/components'
import { Link } from '@mui/material'
import Text from '@/components/Text'
import { ProgramRewardsByCategory } from '@/types/programs-new/programs-new'
import ProgramRewardsList from '@/components/ProgramRewards/program-rewards-list'
import ProgramSignupAuraBackground from './ProgramSignupAuraBackground'
import './styles.scss'

type ProgramSignupIncentiveSectionProps = {
  programId: number | null
  isGeneric: boolean
  programLogoUrl: string | null | undefined
  resolvedSignupText: string
  rewardsByCategory: ProgramRewardsByCategory | null
  totalRewardsDollars: number
  faqUrl: string
  onSignUpClick: () => void
  onExplainOptiwattClick?: () => void
}

const DEFAULT_FAQ_URL = 'https://optiwatt.com/faq'

export default function ProgramSignupIncentiveSection({
  programId,
  isGeneric,
  programLogoUrl,
  resolvedSignupText,
  rewardsByCategory,
  totalRewardsDollars,
  faqUrl,
  onSignUpClick,
  onExplainOptiwattClick,
}: ProgramSignupIncentiveSectionProps) {
  const history = useHistory()

  useEffect(
    function openSheetOnOverscroll() {
      const isAtBottom = () => {
        const scrollTop = Math.max(
          document.documentElement.scrollTop,
          document.body.scrollTop,
          window.scrollY,
        )
        const scrollHeight = Math.max(
          document.documentElement.scrollHeight,
          document.body.scrollHeight,
        )
        const clientHeight = window.innerHeight
        return scrollTop + clientHeight >= scrollHeight - 10
      }

      let atBottomOnLastScroll = false

      const handleScroll = () => {
        atBottomOnLastScroll = isAtBottom()
      }

      const handleWheel = (e: WheelEvent) => {
        if (atBottomOnLastScroll && e.deltaY > 0) {
          onSignUpClick()
        }
      }

      let touchStartY = 0
      let touchStartAtBottom = false
      const handleTouchStart = (e: TouchEvent) => {
        touchStartY = e.touches[0].clientY
        touchStartAtBottom = isAtBottom()
      }

      const handleTouchMove = (e: TouchEvent) => {
        const deltaY = touchStartY - e.touches[0].clientY
        if (touchStartAtBottom && deltaY > 30) {
          onSignUpClick()
        }
      }

      window.addEventListener('scroll', handleScroll, { passive: true })
      window.addEventListener('wheel', handleWheel, { passive: true })
      window.addEventListener('touchstart', handleTouchStart, { passive: true })
      window.addEventListener('touchmove', handleTouchMove, { passive: true })

      return () => {
        window.removeEventListener('scroll', handleScroll)
        window.removeEventListener('wheel', handleWheel)
        window.removeEventListener('touchstart', handleTouchStart)
        window.removeEventListener('touchmove', handleTouchMove)
      }
    },
    [onSignUpClick],
  )

  return (
    <div className="program-signup-incentive relative flex flex-col min-h-screen overflow-hidden">
      <ProgramSignupAuraBackground />

      <div className="relative z-10 flex flex-col flex-1 px-6 pt-8 pb-28 md:px-16 md:pt-16 md:pb-16 md:justify-center box-border">
        {/* Logo Badge */}
        {(isGeneric || programLogoUrl) && (
          <div className="mb-8 flex w-full min-w-0 justify-center md:w-auto md:self-start">
            <div className="flex w-full min-w-0 max-w-full px-6 py-4 items-center justify-center gap-3 rounded-[100px] bg-white md:inline-flex md:w-auto md:min-w-[326px]">
              <img
                src="img/logo-color.webp"
                alt="Optiwatt"
                className={`block object-contain ${
                  isGeneric ? 'h-[24px]' : 'h-[50px] w-[120px]'
                }`}
              />
              {!isGeneric && programLogoUrl && (
                <>
                  <Icon
                    name="Plus"
                    size={14}
                    color="grey-500"
                    strokeWidth={2.75}
                  />
                  <img
                    src={programLogoUrl}
                    alt="Partner"
                    className="block h-[50px] w-[120px] object-contain"
                  />
                </>
              )}
            </div>
          </div>
        )}

        {/* Signup Text */}
        <h1 className="mb-4 break-words font-[inter] text-[32px] md:text-[40px] font-bold leading-[normal] text-[var(--White,#FFF)]">
          {resolvedSignupText}
        </h1>

        {/* Stats Card (generic) or Program Rewards Card (program) */}
        {isGeneric ? (
          <div className="relative box-border w-full min-w-0 max-w-full overflow-hidden rounded-[8px] p-[3px]">
            <div
              aria-hidden={true}
              className="program-signup-border-gradient program-signup-border-gradient--generic pointer-events-none"
            />
            <div className="relative z-[1] rounded-[6px] bg-white p-4">
              <div className="flex flex-col gap-3">
                <div className="flex items-start gap-2">
                  <Icon
                    name="Users"
                    size={20}
                    color="purple-500"
                    strokeWidth={2.25}
                    className="shrink-0"
                  />
                  <p className="m-0 font-[inter] text-[14px] font-medium leading-[20px] text-[#5d5d5d]">
                    <span className="font-bold">20,000+</span> people enrolled
                    in programs
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Icon
                    name="DollarSign"
                    size={20}
                    color="purple-500"
                    strokeWidth={2.25}
                    className="shrink-0"
                  />
                  <p className="m-0 font-[inter] text-[14px] font-medium leading-[20px] text-[#5d5d5d]">
                    Users save an average of{' '}
                    <span className="font-bold">$388</span> per year
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Icon
                    name="Star"
                    size={20}
                    color="purple-500"
                    strokeWidth={2.25}
                    className="shrink-0"
                  />
                  <p className="m-0 font-[inter] text-[14px] font-medium leading-[20px] text-[#5d5d5d]">
                    <span className="font-bold">4.8/5</span> rating on the{' '}
                    <a
                      href="https://apps.apple.com/us/app/optiwatt-local-energy-rewards/id1536047033"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#5d5d5d] !underline"
                    >
                      App Store
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          rewardsByCategory && (
            <div className="relative box-border w-full min-w-0 max-w-full overflow-hidden rounded-[8px] p-[3px]">
              <div
                aria-hidden={true}
                className="program-signup-border-gradient program-signup-border-gradient--program pointer-events-none"
              />
              <div className="relative z-[1] rounded-[6px] bg-white">
                <div className="flex flex-col gap-4 p-4">
                  <Text
                    variant="h3"
                    styled={false}
                    className="font-[inter] text-[16px] font-bold leading-[normal] text-[var(--Grey-900,#343434)]"
                  >
                    Program rewards
                  </Text>
                  <ProgramRewardsList rewardsByCategory={rewardsByCategory} />
                </div>
              </div>
            </div>
          )
        )}

        {/* Divider */}
        <div
          className="my-8 h-px w-full origin-top scale-y-50 bg-white"
          aria-hidden={true}
        />

        {/* How to Apply */}
        <h2 className="mb-6 min-w-0 break-words font-[inter] text-[20px] font-bold leading-[normal] text-[#FFF]">
          {isGeneric
            ? 'How to apply for a program:'
            : 'How to apply for this program:'}
        </h2>
        <div className="flex flex-col gap-6 mb-6">
          <HowToApplyStep
            icon={
              <Icon
                name="User"
                size={20}
                color="purple-500"
                strokeWidth={2.25}
              />
            }
            title="Set up your account"
            description="Create an account, enter your address, and confirm your participation."
          />
          <HowToApplyStep
            icon={
              <Icon
                name="Zap"
                size={20}
                color="purple-500"
                strokeWidth={2.25}
              />
            }
            title="Enroll in minutes"
            description="Our quick and simple enrollment takes less than 2 minutes."
          />
          <HowToApplyStep
            icon={
              <Icon
                name="Gift"
                size={20}
                color="purple-500"
                strokeWidth={2.25}
              />
            }
            title="Get verified and start participating"
            description="Once approved, your device can begin participating in the program."
          />
        </div>

        {/* FAQ Link */}
        <Link
          href={isGeneric ? DEFAULT_FAQ_URL : faqUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="mb-6 flex min-w-0 max-w-full flex-wrap items-center gap-1 !font-[inter] !text-[14px] !font-bold !leading-[24px] !text-[var(--White,#FFF)] !underline !decoration-solid underline-offset-auto [text-decoration-thickness:auto] [text-decoration-skip-ink:none] [text-underline-position:from-font]"
        >
          {isGeneric
            ? 'Questions? Visit our FAQ page'
            : 'Questions? Visit the Program FAQ page'}
          <Icon
            name="ExternalLink"
            size={16}
            color="white"
            strokeWidth={2.25}
          />
        </Link>

        {/* Who is Optiwatt Button */}
        <button
          onClick={() => {
            const isDesktop = window.matchMedia('(min-width: 768px)').matches
            if (isDesktop && onExplainOptiwattClick) {
              onExplainOptiwattClick()
            } else {
              history.push(`/program/${programId}/explain-optiwatt`)
            }
          }}
          className="flex items-center justify-between w-full border border-solid border-white rounded-[20px] px-4 py-4 bg-transparent text-white hover:bg-white/10 transition-colors mt-6 mb-8 box-border font-[inter]"
        >
          <div className="flex items-center gap-3">
            <Icon name="Info" size={20} color="white" strokeWidth={2.25} />
            <span className="break-words font-[inter] text-[12px] font-bold leading-[20px] text-[var(--White,#FFF)]">
              Who is Optiwatt?
            </span>
          </div>
          <Icon
            name="ChevronRight"
            size={16}
            color="white"
            strokeWidth={2.25}
          />
        </button>
      </div>

      {/* Frozen Footer CTA - Mobile Only */}
      <div className="fixed bottom-0 left-0 right-0 z-20 flex flex-col items-stretch md:hidden">
        <div className="flex items-center justify-center gap-2 self-stretch rounded-tl-[20px] rounded-tr-[20px] border-0 bg-white p-6 shadow-none outline-none">
          <button
            type="button"
            onClick={onSignUpClick}
            className="flex w-full cursor-pointer items-center justify-center gap-2 rounded-[20px] border-0 bg-[#af4bfb] p-[20px] font-[inter] text-lg font-semibold text-white shadow-none ring-0 outline-none transition-transform duration-150 active:scale-[0.97] [-webkit-tap-highlight-color:transparent]"
          >
            <span className="text-[14px] font-bold leading-[20px] text-[var(--White,#FFF)]">
              {isGeneric ? 'Sign Up & Start Saving' : 'Sign Up & Apply'}
            </span>
            {!isGeneric && totalRewardsDollars > 0 && (
              <span className="text-[14px] font-bold leading-[20px] text-[var(--White,#FFF)]">{`– Earn $${totalRewardsDollars}+`}</span>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

function HowToApplyStep({
  icon,
  title,
  description,
}: {
  icon: ReactNode
  title: string
  description: string
}) {
  return (
    <div className="flex gap-4 items-start">
      <div className="flex items-center justify-center w-8 h-8 rounded-lg shrink-0 bg-[#f8ecff]">
        {icon}
      </div>
      <div className="min-w-0">
        <p className="m-0 break-words font-[inter] text-[16px] font-bold leading-[normal] text-[var(--White,#FFF)] mb-2">
          {title}
        </p>
        <p className="m-0 mt-1 break-words font-[inter] text-[14px] font-medium leading-[20px] text-[var(--White,#FFF)]">
          {description}
        </p>
      </div>
    </div>
  )
}
